import React, { createContext, useContext, useEffect, useState } from "react";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
  updateProfile,
  updateEmail,
  updatePassword,
  sendPasswordResetEmail,
} from "firebase/auth";
import { auth, db } from "../firebase";
import { doc, getDoc, setDoc, serverTimestamp } from "firebase/firestore";

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  // Fetch user profile from Firestore
  const fetchUserProfile = async (uid) => {
    try {
      console.log("Fetching profile for user:", uid);
      const userDoc = await getDoc(doc(db, "users", uid));
      if (userDoc.exists()) {
        console.log("User profile found:", userDoc.data());
        setUserProfile(userDoc.data());
      } else {
        console.log("No user profile found for:", uid);
        setUserProfile(null);
      }
    } catch (error) {
      console.error("Error fetching user profile:", error);
      setUserProfile(null);
    }
  };

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (currentUser) => {
      console.log("Auth state changed. Current user:", currentUser?.uid);
      setUser(currentUser);
      if (currentUser) {
        await fetchUserProfile(currentUser.uid);
      } else {
        setUserProfile(null);
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sign up function
  const signup = async (email, password, firstName, lastName) => {
    try {
      console.log("Starting signup process for:", email);
      // Create the user account
      const userCredential = await createUserWithEmailAndPassword(
        auth,
        email,
        password
      );
      const user = userCredential.user;

      // Create user profile in Firestore
      const userProfile = {
        userId: user.uid,
        email: email,
        firstName: firstName,
        lastName: lastName,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      };

      // Set the document with user's UID as the document ID
      await setDoc(doc(db, "users", user.uid), userProfile);
      console.log("User profile created in Firestore");

      // Update display name in Firebase Auth
      await updateProfile(user, {
        displayName: `${firstName} ${lastName}`,
      });
      console.log("Auth profile updated with display name");

      // Fetch the profile after creation
      await fetchUserProfile(user.uid);

      return user;
    } catch (error) {
      console.error("Error during signup:", error);
      throw error;
    }
  };

  // Sign in function
  const login = async (email, password) => {
    try {
      const userCredential = await signInWithEmailAndPassword(
        auth,
        email,
        password
      );
      console.log("User logged in:", userCredential.user.uid);
      await fetchUserProfile(userCredential.user.uid);
      return userCredential.user;
    } catch (error) {
      console.error("Login error:", error);
      throw error;
    }
  };

  // Sign out function
  const logout = async () => {
    try {
      await signOut(auth);
      setUserProfile(null);
      console.log("User logged out successfully");
    } catch (error) {
      console.error("Logout error:", error);
      throw error;
    }
  };

  // Update profile function
  const updateUserProfile = async (updates) => {
    if (!user) throw new Error("No user logged in");

    try {
      const userRef = doc(db, "users", user.uid);

      // Update Firestore profile
      await setDoc(
        userRef,
        {
          ...updates,
          updatedAt: serverTimestamp(),
        },
        { merge: true }
      );

      // If email is being updated
      if (updates.email && updates.email !== user.email) {
        await updateEmail(user, updates.email);
      }

      // If display name is being updated
      if (updates.firstName || updates.lastName) {
        const newDisplayName = `${updates.firstName || ""} ${
          updates.lastName || ""
        }`.trim();
        await updateProfile(user, { displayName: newDisplayName });
      }

      // Refresh profile data
      await fetchUserProfile(user.uid);
    } catch (error) {
      console.error("Error updating profile:", error);
      throw error;
    }
  };

  // Change password function
  const changePassword = async (newPassword) => {
    if (!user) throw new Error("No user logged in");
    await updatePassword(user, newPassword);
  };

  // Reset password function
  const resetPassword = async (email) => {
    await sendPasswordResetEmail(auth, email);
  };

  // Get user ID function
  const getUserId = () => {
    return user ? user.uid : null;
  };

  const value = {
    user,
    userProfile,
    login,
    signup,
    logout,
    loading,
    updateUserProfile,
    changePassword,
    resetPassword,
    getUserId,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
